import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Formik, Form } from 'formik';
import lodashValues from 'lodash/fp/values';
import moment from 'moment';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import { DEFAULT_MARKET_ID } from 'common/marketConstants';
import { getOfficeHoursByDate, makeTourDates } from 'common/tourHelpers';

import { USER_ROLE_ADMIN } from 'common/userRoles';
import { SCREEN_ID_ADMIN_TOUR_GUIDE_SCHEDULES } from 'client/constants/screenIds';
import { SR_API_ACTION_ADMIN_GET_TOUR_GUIDE_SCHEDULES } from 'api/actionTypes';

import SrPage from 'client/components/SrPage';
import SrFormikField from 'client/components/SrFormikField';
import ButtonDeprecated from 'client/components/ButtonDeprecated';

import postWithState from 'client/actions/postWithState';
import withTransitionHook from 'client/helpers/withTransitionHook';
import allowedRoles from 'client/helpers/allowedRoles';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import { createFormValidationFn, createInitialValues } from 'client/helpers/formikHelpers';

import branch from 'client/core/branch';
import MarketActions from 'client/actions/Market';

function makeFormFields({ marketList } = {}) {
  const marketChoices = (marketList || []).map((market) => ({
    label: market.displayName,
    value: market.id,
  }));

  const formFields = [
    {
      id: 'marketId',
      type: 'select',
      properties: {
        choices: marketChoices,
        multipleSelection: false,
        allowEmptyArray: false,
      },
    },
    {
      id: 'date',
      type: 'date_picker',
      properties: {
        readOnly: false,
      },
    },
  ];

  return formFields;
}

@withTransitionHook
@withScrollToTop
@allowedRoles({
  roles: [USER_ROLE_ADMIN],
  redirectUrl: '/login',
})
@branch({
  marketsById: ['markets', 'byId'],
})
@withScreenId(SCREEN_ID_ADMIN_TOUR_GUIDE_SCHEDULES)
class AdminTourGuideSchedulesPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Admin - Tour Guide Schedules | Sunroom Rentals',
    section: 'Admin',
  });

  static fetchData(match, inject) {
    return MarketActions.getList(inject);
  }

  static propTypes = {
    marketsById: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      allTourDates: [],
      tourGuideSchedules: [],
      availableTourDates: [],
    };
  }

  _onSubmit = (values) => {
    const { dispatch } = this.props;

    const [[startDate, endDate]] = getOfficeHoursByDate({
      date: momentWithAustinTimezone(values.date),
    });

    const allTourDates = makeTourDates({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    });

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_GET_TOUR_GUIDE_SCHEDULES,
      payload: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        marketId: parseInt(values.marketId, 10),
      },
    }).then(({ tourGuideSchedules, availableTourDates }) => {
      this.setState({
        allTourDates,
        tourGuideSchedules,
        availableTourDates,
      });
    });
  };

  render() {
    const { marketsById } = this.props;
    const { allTourDates, tourGuideSchedules, availableTourDates } = this.state;

    const formFields = makeFormFields({
      marketList: lodashValues(marketsById),
    });

    return (
      <SrPage navbarProps={{ redirectLogoTo: '/admin' }}>
        <div className="AdminTourGuideSchedulesPage width-90">
          <div>
            <Formik
              initialValues={createInitialValues({
                formFields: formFields,
                defaultValues: {
                  marketId: DEFAULT_MARKET_ID,
                  date: momentWithAustinTimezone().toISOString(),
                },
              })}
              validate={createFormValidationFn(formFields)}
              onSubmit={this._onSubmit}
              validateOnChange
              render={(formikBag) => {
                return (
                  <Form>
                    {formFields.map((formField) => (
                      <SrFormikField
                        key={formField.id}
                        formField={formField}
                        formikBag={formikBag}
                      />
                    ))}
                    <ButtonDeprecated type="submit">Load schedules</ButtonDeprecated>
                  </Form>
                );
              }}
            />
          </div>

          <div className="schedules">
            <div className="flex">
              <div className="tourGuideNameRow" />
              {allTourDates.map((tourDate) => (
                <div key={tourDate} className="timeSlot hour">
                  <span>{moment(tourDate).format('h:mma')}</span>
                </div>
              ))}
            </div>
            <div>
              {tourGuideSchedules.map((
                { agentUserId, agentUserName, tourDatesAvailable, tourDatesUnavailable, tourDates }, // eslint-disable-line max-len
              ) => (
                <div key={agentUserId} className="flex">
                  <div className="tourGuideNameRow">
                    <span>{agentUserName}</span>
                  </div>
                  {allTourDates.map((tourDate) => (
                    <div
                      key={tourDate}
                      className={classnames('timeSlot', {
                        available:
                          allTourDates.includes(tourDate) && tourDatesAvailable.includes(tourDate),
                        touring:
                          allTourDates.includes(tourDate) &&
                          tourDatesUnavailable.includes(tourDate),
                        disabled:
                          tourDates.includes(tourDate) &&
                          !tourDatesAvailable.includes(tourDate) &&
                          !tourDatesUnavailable.includes(tourDate), // eslint-disable-line max-len
                      })}
                    />
                  ))}
                </div>
              ))}
            </div>
            <div className="flex timeSlots">
              {availableTourDates.length > 0 && (
                <div className="tourGuideNameRow">
                  <span>Time slots</span>
                </div>
              )}
              {allTourDates.map((tourDate) => (
                <div
                  key={tourDate}
                  className={classnames('timeSlot', {
                    available:
                      allTourDates.includes(tourDate) && availableTourDates.includes(tourDate),
                    notAvailable:
                      allTourDates.includes(tourDate) && !availableTourDates.includes(tourDate),
                  })}
                />
              ))}
            </div>
          </div>
        </div>
      </SrPage>
    );
  }
}

AdminTourGuideSchedulesPage.displayName = 'AdminTourGuideSchedulesPage';

export default AdminTourGuideSchedulesPage;
